import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  forwardRef,
  inject,
  input,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DEFAULT_DEBOUNCE_TIME } from '@nk/utils-lib';
import { debounce, timer } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';
import { HelpPopoverComponent } from '../../help-popover/help-popover.component';

function checkControlValid<K>(formControl: FormControl<K>): boolean {
  return (formControl && formControl.valid) || !(formControl.touched && formControl.dirty);
}

@Component({
  selector: 'nk-search-control',
  templateUrl: './search-control.component.html',
  imports: [ReactiveFormsModule, TranslateModule, HelpPopoverComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchControlComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SearchControlComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchControlComponent implements ControlValueAccessor, AfterViewInit {
  public debounceTime = input(DEFAULT_DEBOUNCE_TIME);
  public id = input('');
  public label = input('');
  public placeholder = input('');
  public helpPopoverTemplate = input<TemplateRef<unknown>>();
  public disabled = input(false);
  public searchBoxStyle = input<'round' | 'square'>('square');
  public validators = input<ValidatorFn[]>([]);
  public validationErrors = output<ValidationErrors | null>();

  public formControl = new FormControl('', { nonNullable: true });
  public errorHighlight = signal(false);
  public isEmptyQuery = signal(true);
  public isDisabled = signal(false);

  private _searchQuery = '';
  private _destroy = inject(DestroyRef);

  constructor() {
    this.formControl.setValidators([this.validate.bind(this)]);
    effect(() => {
      this.isDisabled.set(this.disabled());
    });
    effect(() => {
      this.validators();
      this.formControl.updateValueAndValidity({ emitEvent: false });
    });
  }

  public ngAfterViewInit(): void {
    this.isEmptyQuery.set(!this._searchQuery);
    this.formControl.valueChanges
      .pipe(
        tap((searchQuery: string) => {
          this.isEmptyQuery.set(!searchQuery);
        }),
        debounce((searchQuery) => {
          return !searchQuery ? timer(0) : timer(this.debounceTime());
        }),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroy),
      )
      .subscribe((searchQuery: string) => {
        this.setSearchQuery(searchQuery);
      });
  }

  public onChange = (searchQuery: string): void => {};

  public onTouched = (): void => {};

  public set value(value: string) {}

  public get value(): string {
    return this._searchQuery;
  }

  public setSearchQuery(searchQuery: string): void {
    if (!this.isDisabled()) {
      this._searchQuery = searchQuery;
      this.writeValue(searchQuery);
    }
  }

  public clearInput(): void {
    this.formControl.patchValue('');
  }

  public writeValue(searchQuery: string): void {
    if (searchQuery !== undefined) {
      this._searchQuery = searchQuery;
      this.formControl.setValue(searchQuery, { emitEvent: false });
    }
    const validationErrors = this.validate(this.formControl);
    if (!validationErrors) {
      this.onChange(searchQuery);
    }
    this.validationErrors.emit(validationErrors);
    this.errorHighlight.set(checkControlValid(this.formControl));
  }

  public registerOnChange(fn: (searchQuery: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    const validators = this.validators();
    if (validators?.length) {
      for (const validator of validators) {
        const errors = validator(control);
        if (errors) {
          return errors;
        }
      }
    }
    return null;
  }
}
